import LegalPageLayout from '../components/LegalPageLayout';

export default function TermsPage() {
  return (
    <LegalPageLayout title="Terms & Conditions" lastUpdated="February 2025">
      <h2><span className="section-num">01.</span> ACCEPTANCE OF TERMS</h2>
      <p>
        By accessing or using the AIBL platform, API, website (aibl.money), and any associated services (collectively, the "Service"), you agree to be bound by these Terms and Conditions. If you do not agree to these terms, do not use the Service.
      </p>
      <p>
        AIBL is a decentralized financial infrastructure platform providing wallet, payment, and lending services designed for AI agents and their operators. These terms govern your use of all AIBL products including the API, enaibl lending protocol, and developer tools.
      </p>

      <h2><span className="section-num">02.</span> DEFINITIONS</h2>
      <ul>
        <li><strong>"AIBL"</strong> refers to the AIBL platform, its operators, and associated smart contracts.</li>
        <li><strong>"Agent"</strong> refers to any autonomous AI agent, bot, or automated system interacting with the Service.</li>
        <li><strong>"Operator"</strong> refers to the individual or entity that deploys, manages, or controls an Agent.</li>
        <li><strong>"enaibl"</strong> refers to AIBL's lending protocol enabling agents to borrow ETH against token collateral.</li>
        <li><strong>"Vault"</strong> refers to the multi-signature ETH liquidity pool funding enaibl loans.</li>
        <li><strong>"Collateral"</strong> refers to tokens deposited by an Agent as security for a loan.</li>
        <li><strong>"API"</strong> refers to the AIBL application programming interface.</li>
      </ul>

      <h2><span className="section-num">03.</span> ELIGIBILITY</h2>
      <p>
        You must be at least 18 years old and legally capable of entering into binding agreements in your jurisdiction. By using the Service, you represent that:
      </p>
      <ul>
        <li>You are not located in, or a resident of, any jurisdiction where use of decentralized finance protocols or cryptocurrency is prohibited.</li>
        <li>You are not on any sanctions list maintained by the United States (OFAC), European Union, United Kingdom, or United Nations.</li>
        <li>You understand the risks associated with cryptocurrency, DeFi protocols, and smart contract interactions.</li>
        <li>If operating on behalf of an entity, you have the authority to bind that entity to these terms.</li>
      </ul>

      <h2><span className="section-num">04.</span> DESCRIPTION OF SERVICE</h2>
      <p>AIBL provides:</p>
      <ul>
        <li>Multi-chain wallet infrastructure for AI agents via API</li>
        <li>Payment and transaction execution across 20+ blockchain networks</li>
        <li>The enaibl lending protocol, enabling agents to borrow ETH against token holdings</li>
        <li>Developer tools, documentation, and integration resources</li>
      </ul>
      <p>
        The Service operates on public blockchain networks. AIBL does not control the underlying blockchain infrastructure and is not responsible for network congestion, gas fees, failed transactions caused by network conditions, or blockchain reorganizations.
      </p>

      <h2><span className="section-num">05.</span> API ACCESS AND USAGE</h2>
      <ul>
        <li>API access is granted at AIBL's discretion and may be revoked at any time.</li>
        <li>You agree not to abuse, overload, or attempt to exploit the API.</li>
        <li>Rate limits apply. Exceeding rate limits may result in temporary or permanent suspension.</li>
        <li>API credentials are confidential. You are responsible for securing your keys.</li>
        <li>AIBL reserves the right to modify API endpoints, parameters, and functionality with reasonable notice.</li>
      </ul>

      <h2><span className="section-num">06.</span> ENAIBL LENDING PROTOCOL</h2>
      
      <h3>6.1 BORROWING</h3>
      <ul>
        <li>Agents may borrow ETH by depositing eligible token collateral.</li>
        <li>Loan-to-value (LTV) ratios are determined by AIBL based on token market cap, liquidity, and risk assessment.</li>
        <li>Interest is calculated daily and prepaid from the loan amount at origination.</li>
        <li>Origination fees apply and are deducted from the loan amount.</li>
      </ul>

      <h3>6.2 COLLATERAL</h3>
      <ul>
        <li>Eligible collateral includes tokens deployed via verified factory contracts (Clanker, Bankr, Zora, Flaunch) meeting minimum liquidity requirements.</li>
        <li>AIBL reserves the right to modify collateral eligibility criteria at any time.</li>
        <li>Collateral values are determined by on-chain price data and may fluctuate.</li>
      </ul>

      <h3>6.3 LIQUIDATION</h3>
      <ul>
        <li>If a loan's health factor falls below the liquidation threshold, AIBL may liquidate collateral to recover the loan amount.</li>
        <li>Liquidation is automatic and irreversible.</li>
        <li>AIBL is not liable for losses resulting from liquidation, including losses caused by rapid price movements, oracle delays, or market volatility.</li>
      </ul>

      <h3>6.4 VAULT AND LENDING</h3>
      <ul>
        <li>ETH deposited into the vault is used to fund loans and is subject to utilization risk.</li>
        <li>Lender yields are variable and depend on vault utilization rates.</li>
        <li>AIBL does not guarantee any specific yield or return.</li>
      </ul>

      <h2><span className="section-num">07.</span> FEES</h2>
      <ul>
        <li>API transaction fees: 0.1% per call (subject to change with notice)</li>
        <li>enaibl origination fee: 2% of loan amount</li>
        <li>enaibl daily interest: Up to 1% per day depending on tier</li>
        <li>Network gas fees are the responsibility of the user/agent and are not controlled by AIBL</li>
        <li>AIBL reserves the right to modify fee structures with 14 days notice</li>
      </ul>

      <h2><span className="section-num">08.</span> RISKS AND DISCLAIMERS</h2>
      <p>YOU ACKNOWLEDGE AND ACCEPT THE FOLLOWING RISKS:</p>
      <ul>
        <li><strong>Smart contract risk:</strong> Despite auditing efforts, smart contracts may contain bugs or vulnerabilities that could result in loss of funds.</li>
        <li><strong>Market risk:</strong> Cryptocurrency prices are volatile. Collateral values can decline rapidly, triggering liquidation.</li>
        <li><strong>Liquidity risk:</strong> Token liquidity can dry up, making it difficult or impossible to liquidate collateral at fair value.</li>
        <li><strong>Regulatory risk:</strong> The legal status of DeFi protocols varies by jurisdiction and may change.</li>
        <li><strong>Technology risk:</strong> Blockchain networks, oracles, and infrastructure may experience downtime, congestion, or failures.</li>
        <li><strong>Meme token risk:</strong> Meme tokens and low-cap tokens carry extreme volatility and rug-pull risk. AIBL's verification does not constitute an endorsement of any token.</li>
      </ul>
      <div className="caps-warning">
        THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
      </div>

      <h2><span className="section-num">09.</span> LIMITATION OF LIABILITY</h2>
      <div className="caps-warning">
        TO THE MAXIMUM EXTENT PERMITTED BY LAW, AIBL AND ITS OPERATORS, CONTRIBUTORS, AND AFFILIATES SHALL NOT BE LIABLE FOR:
        <br /><br />
        • Any loss of funds, tokens, or digital assets<br />
        • Any indirect, incidental, special, consequential, or punitive damages<br />
        • Any loss of profits, revenue, data, or business opportunities<br />
        • Any damages arising from liquidation events<br />
        • Any damages resulting from unauthorized access to your account or API credentials<br />
        • Any damages caused by third-party services, blockchain networks, or oracle failures<br /><br />
        TOTAL LIABILITY SHALL NOT EXCEED THE FEES PAID BY YOU TO AIBL IN THE 12 MONTHS PRECEDING THE CLAIM.
      </div>

      <h2><span className="section-num">10.</span> PROHIBITED USES</h2>
      <p>You agree NOT to use the Service to:</p>
      <ul>
        <li>Violate any applicable law or regulation</li>
        <li>Engage in money laundering, terrorist financing, or sanctions evasion</li>
        <li>Manipulate markets, including wash trading or price manipulation</li>
        <li>Exploit smart contract vulnerabilities or conduct flash loan attacks against the protocol</li>
        <li>Attempt to gain unauthorized access to AIBL systems or other users' accounts</li>
        <li>Use the Service to facilitate fraud, scams, or rug pulls</li>
        <li>Reverse engineer, decompile, or disassemble any part of the Service</li>
        <li>Interfere with or disrupt the Service or connected networks</li>
      </ul>

      <h2><span className="section-num">11.</span> INTELLECTUAL PROPERTY</h2>
      <ul>
        <li>The AIBL name, logo, brand elements, and proprietary technology are owned by AIBL.</li>
        <li>API documentation and developer tools are provided for integration purposes only.</li>
        <li>You may not use AIBL branding without prior written consent.</li>
        <li>Open-source components are governed by their respective licenses.</li>
      </ul>

      <h2><span className="section-num">12.</span> ACCOUNT TERMINATION</h2>
      <p>
        AIBL reserves the right to suspend or terminate your access to the Service at any time, with or without cause, including but not limited to:
      </p>
      <ul>
        <li>Violation of these terms</li>
        <li>Suspected fraudulent or illegal activity</li>
        <li>API abuse or excessive load</li>
        <li>Legal or regulatory requirements</li>
      </ul>
      <p>
        Upon termination, outstanding loans must be repaid. Unretrieved collateral will be handled according to the liquidation process.
      </p>

      <h2><span className="section-num">13.</span> MODIFICATIONS</h2>
      <p>
        AIBL may modify these terms at any time. Material changes will be posted on the website with an updated "Last updated" date. Continued use of the Service after modifications constitutes acceptance of the updated terms.
      </p>

      <h2><span className="section-num">14.</span> GOVERNING LAW AND DISPUTES</h2>
      <p>
        These terms are governed by applicable law. Any disputes arising from or related to these terms or the Service shall be resolved through binding arbitration. You waive the right to participate in class action lawsuits or class-wide arbitration.
      </p>

      <h2><span className="section-num">15.</span> CONTACT</h2>
      <p>For questions about these terms:</p>
      <ul>
        <li>Email: <a href="mailto:hello@aibl.money">hello@aibl.money</a></li>
        <li>Website: <a href="https://aibl.money">aibl.money</a></li>
        <li>Discord: <a href="https://discord.gg/aibl">discord.gg/aibl</a></li>
      </ul>
    </LegalPageLayout>
  );
}
